import { useEffect } from 'react';
import { t } from './i18n';

/**
 * Keeps the tab title and description in step with client-side navigation.
 *
 * The server already sends the right head for the URL that was *typed* — that
 * is what crawlers and link previews read, and it is in api/src/http/seo.ts.
 * This is the other half: once React takes over routing, the document keeps
 * whatever head the first page arrived with, so moving from a search to a
 * listing leaves the old title in the tab and in the browser history.
 *
 * Nothing here matters to a crawler. It matters to somebody with nine tabs open
 * trying to find the flat they were looking at.
 */

const BRAND = 'Bedly';
const FALLBACK = `${BRAND} — ${t('Colocations, chambres et locations au Maroc')}`;

const setMeta = (selector: string, content: string): void => {
  const tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (tag) tag.content = content;
};

export function useSeo(seo: { title?: string | null; description?: string | null }): void {
  const { title, description } = seo;

  useEffect(() => {
    // Nothing yet — a page still loading should keep the server's head rather
    // than flashing a placeholder into the tab and the history entry.
    if (!title) return;

    const full = title.includes(BRAND) ? title : `${title} — ${BRAND}`;
    document.title = full;
    setMeta('meta[property="og:title"]', full);

    if (description) {
      setMeta('meta[name="description"]', description);
      setMeta('meta[property="og:description"]', description);
    }

    return () => {
      document.title = FALLBACK;
    };
  }, [title, description]);
}
